import { createAsyncThunk } from '@reduxjs/toolkit';
import { notification } from 'antd';
import lockr from 'lockr';
import axios from 'axios';
import { fetchApi, fetchApiAuth } from '../utils/api';
import {
    School,
    SchoolClassroom,
    SchoolContact,
} from './types';
import constants from '../constants';

// Define ResponseProps type if it doesn't exist
interface ResponseProps {
    data: any;
}

const {
    LOCALSTORAGE_EXPIRES_KEY,
    AUTH_KEY,
} = constants;

export const updateSchool = createAsyncThunk(
    'school/update-school',
    async (payload:School) => {
        const response:ResponseProps = await fetchApiAuth({
            method: 'POST',
            url: 'v1/school/update-school',
            body: payload,
        });
        return response.data;
    },
);

export const getSchools = createAsyncThunk(
    'school/get-schools',
    async (payload: any) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/get-schools',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const createSchool = createAsyncThunk(
    'school/create-school',
    async (payload: { name: string; type: string }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/create-school',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSingleSchool = createAsyncThunk(
    'school/get-single-school',
    async (payload: any) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/get-single-school',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSchoolClassrooms = createAsyncThunk(
    'school/get-school-classrooms',
    async (payload: { school_id?: number; search?: string; classroom_type?: number }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/get-school-classrooms',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const createOrUpdateSchoolClassroom = createAsyncThunk(
    'school/create-or-update-school-classroom',
    async (payload: SchoolClassroom) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/create-or-update-school-classroom',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSchoolClassroomStatistics = createAsyncThunk(
    'school/get-school-classroom-statistics',
    async (payload: { school_id: number }) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/get-school-classrooms-statistics',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSchoolClassroomStatisticsOverall = createAsyncThunk(
    'school/get-school-classroom-statistics-overall',
    async () => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/get-classrooms-statistics-overall',
                body: {},
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const getSchoolContacts = createAsyncThunk(
    'school/get-school-contacts',
    async (schoolId: number) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/get-school-contacts',
                body: { school_id: schoolId },
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const createSchoolContact = createAsyncThunk(
    'school/create-school-contact',
    async (payload: Omit<SchoolContact, 'id' | 'created_at' | 'updated_at'>) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/create-school-contact',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);

export const updateSchoolContact = createAsyncThunk(
    'school/update-school-contact',
    async (payload: Omit<SchoolContact, 'created_at' | 'updated_at'>) => {
        try {
            const response:ResponseProps = await fetchApiAuth({
                method: 'POST',
                url: 'v1/school/update-school-contact',
                body: payload,
            });
            return response.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const errorMessage = (error.response?.data.message as string) || 'Something went wrong';
                notification.error({
                    message: 'Error',
                    description: errorMessage,
                });
            }
            throw error;
        }
    },
);
